import { isNativePlatform } from "@/plugins/growthbook";
import { useForceUpdateStore } from "@/stores/forceUpdate.store";

const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=com.hungryhub.partnerapp";
const IOS_STORE_URL = "https://apps.apple.com/br/app/hungry-hub-partner-portal-app/id6504741104";

const MIN_FETCH_INTERVAL_S = 60 * 60;

function parseSemver(v) {
  if (!v) return null;
  const parts = String(v)
    .split(".")
    .map((n) => parseInt(n, 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return [parts[0], parts[1], parts[2]];
}

function isRemoteGreater(remoteParts, currentParts) {
  for (let i = 0; i < 3; i++) {
    if (remoteParts[i] > currentParts[i]) return true;
    if (remoteParts[i] < currentParts[i]) return false;
  }
  return false;
}

export function useForceUpdate() {
  const store = useForceUpdateStore();

  async function getCurrentVersion() {
    try {
      const { App } = await import("@capacitor/app");
      const info = await App.getInfo();
      return info?.version || null;
    } catch (e) {
      console.warn("[force-update] getInfo failed", e);
      return null;
    }
  }

  async function fetchRemoteConfig() {
    const { FirebaseRemoteConfig } = await import("@capacitor-firebase/remote-config");
    try {
      await FirebaseRemoteConfig.setSettings({
        fetchTimeoutInSeconds: 60,
        minimumFetchIntervalInSeconds: MIN_FETCH_INTERVAL_S,
      });
    } catch (e) {
      console.warn("[force-update] setSettings failed", e);
    }
    await FirebaseRemoteConfig.fetchAndActivate();
  }

  function getPlatformStoreUrl() {
    if (typeof window === "undefined") return null;
    const cap = window.Capacitor;
    const platform = cap?.getPlatform?.();
    if (platform === "ios") return IOS_STORE_URL;
    if (platform === "android") return ANDROID_STORE_URL;
    return null;
  }

  async function check() {
    if (!isNativePlatform()) return;

    const currentVersion = await getCurrentVersion();
    const currentParts = parseSemver(currentVersion);
    if (!currentParts) {
      console.warn("[force-update] no current version, skipping check");
      return;
    }

    let appVersion;
    let forceUpdate;
    try {
      await fetchRemoteConfig();
      const { FirebaseRemoteConfig } = await import("@capacitor-firebase/remote-config");
      const appVersionResult = await FirebaseRemoteConfig.getString({ key: "app_version" });
      const forceUpdateResult = await FirebaseRemoteConfig.getBoolean({ key: "force_update" });
      appVersion = appVersionResult?.value ?? "";
      forceUpdate = forceUpdateResult?.value ?? false;
    } catch (e) {
      console.warn("[force-update] remote config fetch failed, skipping", e);
      return;
    }

    const remoteParts = parseSemver(appVersion);
    if (!remoteParts) {
      console.warn("[force-update] no/invalid app_version in remote config");
      return;
    }

    if (!isRemoteGreater(remoteParts, currentParts)) {
      store.setRequired({ required: false, hard: true, minVersion: appVersion });
      return;
    }

    store.setRequired({
      required: true,
      hard: Boolean(forceUpdate),
      minVersion: appVersion,
    });
  }

  function openStore() {
    const url = getPlatformStoreUrl();
    if (!url) return;
    window.open(url, "_system", "noopener,noreferrer");
  }

  return { check, openStore, getPlatformStoreUrl };
}
